/**
 * Coming back to a task from its company (Romaric, Prism task #270,
 * 2026-10-26: "on doit aller sur la fiche de l'entreprise et quand on fait
 * retour, on ne revient pas sur la tâche").
 *
 * Two pieces of router history state, both kept out of the URL:
 * - `openTask` sits on the history entry of a page whose task drawer is
 *   open. Coming back to that entry (browser Back, or the company page's
 *   "Back to task") reopens the drawer. `host` names the drawer that wrote
 *   it, since one page can hold several (Tasks and its per-project boards).
 * - `fromTask` sits on the company page entry that a task's link pushed
 *   (a drawer's, a dashboard row's). The company page then leads back to the
 *   previous entry instead of to Companies.
 * - `controls` sits beside `openTask`: the page's own scope, view and
 *   filters, so that it comes back as it was left, not as its default view
 *   (the drawer of a Kanban board only exists in that view).
 */

/** What a reopened drawer shows before its page's list has loaded. */
export interface TaskLike {
  id: number;
  title: string;
}

/** The task a company page was opened from. */
export interface FromTask {
  id: number;
  title: string;
  /** The back button's label: where the task was shown. */
  back: string;
}

const BACK_TO_TASK = "Back to task";

type State = Record<string, unknown>;

function asState(state: unknown): State {
  return state !== null && typeof state === "object" && !Array.isArray(state)
    ? (state as State)
    : {};
}

function isTask(value: unknown): value is TaskLike {
  if (value === null || typeof value !== "object") return false;
  const task = value as Partial<TaskLike>;
  return typeof task.id === "number" && typeof task.title === "string";
}

/** The task whose drawer `host` left open on this history entry, if any. */
export function rememberedTask<T extends TaskLike>(
  state: unknown,
  host: string,
): T | null {
  const open = asState(asState(state).openTask);
  return open.host === host && isTask(open.task) ? (open.task as T) : null;
}

/** This entry's state with `task` open in `host`'s drawer, or none open. */
export function withOpenTask(
  state: unknown,
  host: string,
  task: TaskLike | null,
): State {
  const next = { ...asState(state) };
  delete next.openTask;
  if (task) next.openTask = { host, task };
  return next;
}

/** The state a task's company link gives the company page it opens.
 *  `back` names the page left, when it is not the task's drawer. */
export function fromTaskState(task: TaskLike, back = BACK_TO_TASK): State {
  return { fromTask: { id: task.id, title: task.title, back } };
}

/** The task this company page was opened from, if it was. */
export function cameFromTask(state: unknown): FromTask | null {
  const from = asState(state).fromTask;
  if (!isTask(from)) return null;
  const back = (from as { back?: unknown }).back;
  return {
    id: from.id,
    title: from.title,
    back: typeof back === "string" && back ? back : BACK_TO_TASK,
  };
}

/** The controls `page` was left with on this history entry, if any. */
export function rememberedControls<T extends object>(
  state: unknown,
  page: string,
): Partial<T> | null {
  const kept = asState(asState(state).controls);
  return kept.page === page && kept.value !== null && typeof kept.value === "object"
    ? (kept.value as Partial<T>)
    : null;
}

/** This entry's state with `page`'s current controls. */
export function withControls(state: unknown, page: string, value: object): State {
  return { ...asState(state), controls: { page, value } };
}
